import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import KanbanView from './KanbanBoard';
import styles from './css/index.module.css';
import DonutChart from '../Overview/DonutChart';
import { CustomAvatar } from './index';
import { PatchTask } from '../../redux/taskSlice';
import { toast } from 'react-toastify';
import PauseTaskConfirmModal from '../CommonComponents/DoubleConfirmationModal/PauseTaskConfirmModal';
import RecalculateScheduleModal from '../CommonComponents/DoubleConfirmationModal/RecalculateScheduleModal';
import {
    requestStatusScheduleMutation,
    shouldConfirmPauseCancelResume,
    statusNeedsSchedulePreview,
    statusPatchBody,
} from '../../utils/taskScheduleMutation';
import { normalizeTaskStatus } from '../../utils/taskSchedulingUtils';
import { Filter20Regular, Delete16Regular } from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList, Tooltip } from '@fluentui/react-components';

const Reporting = ({ onOpenSidebar }) => {
    const dispatch = useDispatch();
    const { taskData } = useSelector(state => state.task);
    const { projectUsersAll } = useSelector(state => state.team);

    // Filter state
    const [filters, setFilters] = React.useState([{ id: 1, column: '', value: '' }]);
    const [pendingStatusChange, setPendingStatusChange] = React.useState(null);
    const [statusChangeLoading, setStatusChangeLoading] = React.useState(false);
    const [pendingScheduleChange, setPendingScheduleChange] = React.useState(null);
    const [scheduleChangeLoading, setScheduleChangeLoading] = React.useState(false);
    const [previewTaskId, setPreviewTaskId] = React.useState(null);

    const applyStatusPatch = async (task, newStatus, { cascadeStatus = false, updateOnly, resumeDownstream = false } = {}) => {
        const updateBody = statusPatchBody(task, newStatus);
        if (cascadeStatus) updateBody.cascade_status = true;
        if (resumeDownstream) updateBody.resume_downstream = true;
        if (updateOnly !== undefined) updateBody.update_only = updateOnly;
        await dispatch(
            PatchTask({
                id: task.id,
                body: updateBody,
                suppressToast: true,
            }),
        );
    };

    const applySchedulePatch = async (task, payload, { updateOnly, adjustDownstreamDeadlines } = {}) => {
        const body = { ...payload, update_only: updateOnly };
        if (adjustDownstreamDeadlines) body.adjust_downstream_deadlines = true;
        await dispatch(PatchTask({ body, id: task.id, suppressToast: true }));
    };

    // Get column options for filtering
    const getColumnOptions = () => [
        { value: 'status', label: 'Status' },
        { value: 'priority', label: 'Priority' },
        { value: 'workstream', label: 'Workstream' },
        { value: 'tag', label: 'Tag' },
        { value: 'assigned_to', label: 'Assigned To' },
    ];

    // Get value options based on selected column
    const getValueOptions = column => {
        if (!column) return [];

        const uniqueValues = [
            ...new Set(
                taskData.map(task => {
                    switch (column) {
                        case 'status':
                            return task.status || 'NOT_STARTED';
                        case 'priority':
                            return task.priority || 'MEDIUM';
                        case 'workstream':
                            return task.workstream_repr || 'Unknown';
                        case 'tag':
                            return task.tag_repr || 'Unknown';
                        case 'assigned_to':
                            return task.assigned_to_name || 'Unassigned';
                        default:
                            return '';
                    }
                }),
            ),
        ];

        return uniqueValues.map(value => {
            let label = value;

            // Format labels for better display
            switch (column) {
                case 'status':
                    // Convert status values to readable format
                    const statusMap = {
                        NOT_STARTED: 'Not Started',
                        WORKING_ON_IT: 'Working on it',
                        STUCK: 'Blocked',
                        COMPLETED: 'Completed',
                        CANCELLED: 'Cancelled',
                        BLOCKED: 'Blocked',
                        PAUSED: 'Paused',
                        UNDEFINED: 'Undefined',
                    };
                    label = statusMap[value] || value;
                    break;
                case 'priority':
                    // Convert priority values to readable format
                    const priorityMap = {
                        HIGH: 'High',
                        MEDIUM: 'Medium',
                        LOW: 'Low',
                    };
                    label = priorityMap[value] || value;
                    break;
                case 'workstream':
                case 'tag':
                case 'assigned_to':
                    // These are already in readable format
                    label = value;
                    break;
                default:
                    label = value;
            }

            return { value, label };
        });
    };

    // Filter tasks based on current filters
    const getFilteredTasks = () => {
        if (!taskData) return [];

        return taskData.filter(task => {
            return filters.every(filter => {
                if (!filter.column || !filter.value) return true;

                let taskValue = '';
                switch (filter.column) {
                    case 'status':
                        taskValue = task.status || 'NOT_STARTED';
                        break;
                    case 'priority':
                        taskValue = task.priority || 'MEDIUM';
                        break;
                    case 'workstream':
                        taskValue = task.workstream_repr || 'Unknown';
                        break;
                    case 'tag':
                        taskValue = task.tag_repr || 'Unknown';
                        break;
                    case 'assigned_to':
                        taskValue = task.assigned_to_name || 'Unassigned';
                        break;
                    default:
                        return true;
                }

                return taskValue === filter.value;
            });
        });
    };

    // Filter management functions
    const handleAddFilter = () => {
        const newId = Math.max(...filters.map(f => f.id), 0) + 1;
        setFilters([...filters, { id: newId, column: '', value: '' }]);
    };

    const handleUpdateFilter = (id, field, value) => {
        setFilters(filters.map(filter => (filter.id === id ? { ...filter, [field]: value, ...(field === 'column' ? { value: '' } : {}) } : filter)));
    };

    const handleRemoveFilter = id => {
        if (filters.length > 1) {
            setFilters(filters.filter(filter => filter.id !== id));
        }
    };

    const handleClearAllFilters = () => {
        setFilters([{ id: 1, column: '', value: '' }]);
    };

    // Get filtered tasks
    const filteredTasks = getFilteredTasks();

    // Calculate task statistics for the 4 main cards (filtered)
    const calculateTaskStats = () => {
        if (!filteredTasks || filteredTasks.length === 0) {
            return {
                totalTasks: 0,
                inProgress: 0,
                stuck: 0,
                completed: 0,
            };
        }

        const totalTasks = filteredTasks.length;
        const inProgress = filteredTasks.filter(task => task.status === 'WORKING_ON_IT').length;
        const stuck = filteredTasks.filter(task => task.status === 'BLOCKED' || task.status === 'STUCK').length;
        const completed = filteredTasks.filter(task => task.status === 'COMPLETED').length;

        return {
            totalTasks,
            inProgress,
            stuck,
            completed,
        };
    };

    // Calculate statistics for donut chart and owner stats (unfiltered)
    const calculateUnfilteredStats = () => {
        if (!taskData || taskData.length === 0) {
            return {
                notStarted: 0,
                statusData: [0, 0, 0, 0, 0],
                statusPercentages: [0, 0, 0, 0, 0],
            };
        }

        const totalTasks = taskData.length;
        const inProgress = taskData.filter(task => task.status === 'WORKING_ON_IT').length;
        const stuck = taskData.filter(task => task.status === 'BLOCKED' || task.status === 'STUCK').length;
        const completed = taskData.filter(task => task.status === 'COMPLETED').length;
        const notStarted = taskData.filter(task => !task.status || task.status === 'NOT_STARTED').length;
        const cancelled = taskData.filter(task => task.status === 'CANCELLED').length;
        const undefinedStatus = taskData.filter(task => task.status === 'UNDEFINED').length;

        const statusData = [notStarted, inProgress, stuck, completed, cancelled];
        const statusPercentages = statusData.map(count => (totalTasks > 0 ? Math.round((count / totalTasks) * 100) : 0));

        return {
            notStarted,
            cancelled,
            undefined: undefinedStatus,
            statusData,
            statusPercentages,
        };
    };

    // Calculate tasks by owner (unfiltered)
    const calculateTasksByOwner = () => {
        if (!taskData || !projectUsersAll) return [];

        const ownerStats = {};

        // Initialize stats for all project users
        projectUsersAll.forEach(user => {
            ownerStats[user.value.user_id] = {
                name: user.value.full_name,
                notStarted: 0,
                completed: 0,
                inProgress: 0,
                stuck: 0,
                cancelled: 0,
                undefinedStatus: 0,
                total: 0,
            };
        });

        // Count tasks by owner and status
        taskData.forEach(task => {
            if (task.assigned_to && ownerStats[task.assigned_to]) {
                ownerStats[task.assigned_to].total++;

                switch (task.status) {
                    case 'NOT_STARTED':
                        ownerStats[task.assigned_to].notStarted++;
                        break;
                    case 'COMPLETED':
                        ownerStats[task.assigned_to].completed++;
                        break;
                    case 'WORKING_ON_IT':
                        ownerStats[task.assigned_to].inProgress++;
                        break;
                    case 'STUCK':
                    case 'BLOCKED':
                        ownerStats[task.assigned_to].stuck++;
                        break;
                    case 'CANCELLED':
                        ownerStats[task.assigned_to].cancelled++;
                        break;
                    case 'UNDEFINED':
                        ownerStats[task.assigned_to].undefinedStatus++;
                        break;
                    default:
                        // Count as not started for any other status
                        ownerStats[task.assigned_to].notStarted++;
                        break;
                }
            }
        });

        // Convert to array and filter out users with no tasks
        return Object.values(ownerStats)
            .filter(owner => owner.total > 0)
            .sort((a, b) => b.total - a.total)
            .slice(0, 5); // Show top 5 users
    };

    // Calculate workstream-level progress (unfiltered, uses all taskData)
    const calculateWorkstreamStats = () => {
        if (!taskData || taskData.length === 0) {
            return [];
        }

        const workstreamMap = {};

        taskData.forEach(task => {
            const name = task.workstream_repr || 'Unspecified';

            if (!workstreamMap[name]) {
                workstreamMap[name] = {
                    name,
                    total: 0,
                    completed: 0,
                    completedByOwner: {},
                    inProgress: 0,
                    inProgressByOwner: {},
                    stuck: 0,
                    stuckByOwner: {},
                    notStarted: 0,
                    notStartedByOwner: {},
                    cancelled: 0,
                    cancelledByOwner: {},
                    lastUpdated: null,
                };
            }

            const workstream = workstreamMap[name];
            const ownerName = task.assigned_to_name || 'Unassigned';

            workstream.total += 1;

            switch (task.status) {
                case 'COMPLETED': {
                    workstream.completed += 1;
                    if (!workstream.completedByOwner[ownerName]) {
                        workstream.completedByOwner[ownerName] = 0;
                    }
                    workstream.completedByOwner[ownerName] += 1;
                    break;
                }
                case 'WORKING_ON_IT':
                    workstream.inProgress += 1;
                    if (!workstream.inProgressByOwner[ownerName]) {
                        workstream.inProgressByOwner[ownerName] = 0;
                    }
                    workstream.inProgressByOwner[ownerName] += 1;
                    break;
                case 'STUCK':
                case 'BLOCKED':
                    workstream.stuck += 1;
                    if (!workstream.stuckByOwner[ownerName]) {
                        workstream.stuckByOwner[ownerName] = 0;
                    }
                    workstream.stuckByOwner[ownerName] += 1;
                    break;
                case 'CANCELLED': {
                    workstream.cancelled += 1;
                    if (!workstream.cancelledByOwner[ownerName]) {
                        workstream.cancelledByOwner[ownerName] = 0;
                    }
                    workstream.cancelledByOwner[ownerName] += 1;
                    break;
                }
                case 'NOT_STARTED':
                default: {
                    workstream.notStarted += 1;
                    if (!workstream.notStartedByOwner[ownerName]) {
                        workstream.notStartedByOwner[ownerName] = 0;
                    }
                    workstream.notStartedByOwner[ownerName] += 1;
                    break;
                }
            }

            const candidateDate = task.updated_at || task.last_updated || task.created_at;
            if (candidateDate) {
                if (!workstream.lastUpdated || new Date(candidateDate) > new Date(workstream.lastUpdated)) {
                    workstream.lastUpdated = candidateDate;
                }
            }
        });

        return Object.values(workstreamMap).sort((a, b) => a.name.localeCompare(b.name));
    };

    const formatWorkstreamLastUpdated = timestamp => {
        if (!timestamp) return '-- --';

        try {
            const date = new Date(timestamp);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
        } catch (error) {
            console.error('Error formatting workstream last updated date:', error);
            return '-- --';
        }
    };

    const stats = calculateTaskStats();
    const unfilteredStats = calculateUnfilteredStats();
    const ownerStats = calculateTasksByOwner();
    const workstreamStats = calculateWorkstreamStats();
    const taskStatusLegendItems = [
        { label: 'Not Started', color: '#c0c5d9', percentage: unfilteredStats.statusPercentages[0], count: unfilteredStats.statusData[0] },
        { label: 'Working on it', color: 'var(--RAG-Amber)', percentage: unfilteredStats.statusPercentages[1], count: unfilteredStats.statusData[1] },
        { label: 'Blocked', color: 'var(--RAG-Red)', percentage: unfilteredStats.statusPercentages[2], count: unfilteredStats.statusData[2] },
        { label: 'Completed', color: 'var(--RAG-Green)', percentage: unfilteredStats.statusPercentages[3], count: unfilteredStats.statusData[3] },
        { label: 'Cancelled', color: '#989BAD', percentage: unfilteredStats.statusPercentages[4], count: unfilteredStats.statusData[4] },
    ]
        .filter(item => item.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
    const fullTaskStatusChartItems = [
        { color: '#C0C6D8', count: unfilteredStats.statusData[0] },
        { color: '#FFD000', count: unfilteredStats.statusData[1] },
        { color: '#B50823', count: unfilteredStats.statusData[2] },
        { color: '#1B8C5A', count: unfilteredStats.statusData[3] },
        { color: '#989BAD', count: unfilteredStats.statusData[4] },
    ].filter(item => item.count > 0);

    // Handle status change from Kanban board
    const handleStatusChange = async (task, newStatus) => {
        const normalized = normalizeTaskStatus(newStatus);
        const prev = normalizeTaskStatus(task.status);

        const pauseCancelResume = shouldConfirmPauseCancelResume(normalized, prev);
        if (pauseCancelResume) {
            setPendingStatusChange({
                task,
                newStatus: normalized,
                action: pauseCancelResume.action,
            });
            return;
        }

        if (statusNeedsSchedulePreview(normalized, prev)) {
            try {
                await requestStatusScheduleMutation({
                    task,
                    newStatus: normalized,
                    setPending: setPendingScheduleChange,
                    applyPatch: applySchedulePatch,
                    setPreviewTaskId,
                });
            } catch (error) {
                console.error('Failed to update task status:', error);
            }
            return;
        }

        try {
            await applyStatusPatch(task, normalized, { updateOnly: true });
            toast.success('Task status updated successfully');
        } catch (error) {
            console.error('Failed to update task status:', error);
            toast.error('Failed to update task status');
        }
    };

    const handleStatusThisOnly = async () => {
        if (!pendingStatusChange || statusChangeLoading) return;
        setStatusChangeLoading(true);
        try {
            const { task, newStatus, action } = pendingStatusChange;
            await applyStatusPatch(task, newStatus, {
                cascadeStatus: false,
                updateOnly: action === 'resume',
            });
            setPendingStatusChange(null);
            toast.success('Task status updated successfully');
        } catch (error) {
            console.error('Failed to update task status:', error);
            toast.error('Failed to update task status');
        } finally {
            setStatusChangeLoading(false);
        }
    };

    const handleStatusWithFollowing = async () => {
        if (!pendingStatusChange || statusChangeLoading) return;
        setStatusChangeLoading(true);
        try {
            const { task, newStatus, action } = pendingStatusChange;
            if (action === 'resume') {
                await applyStatusPatch(task, newStatus, {
                    cascadeStatus: true,
                    resumeDownstream: true,
                    updateOnly: false,
                });
            } else {
                await applyStatusPatch(task, newStatus, { cascadeStatus: true });
            }
            setPendingStatusChange(null);
            toast.success('Task status updated successfully');
        } catch (error) {
            console.error('Failed to update task status:', error);
            toast.error('Failed to update task status');
        } finally {
            setStatusChangeLoading(false);
        }
    };

    const applyScheduleChange = async ({ updateOnly, adjustDownstreamDeadlines = false } = {}) => {
        if (!pendingScheduleChange || scheduleChangeLoading) return;
        setScheduleChangeLoading(true);
        try {
            await applySchedulePatch(pendingScheduleChange.task, pendingScheduleChange.payload, {
                updateOnly,
                adjustDownstreamDeadlines,
            });
            setPendingScheduleChange(null);
            toast.success('Task status updated successfully');
        } catch (error) {
            console.error('Failed to update task status:', error);
        } finally {
            setScheduleChangeLoading(false);
        }
    };

    // Handle task click
    const handleTaskClick = task => {
        console.log('Task clicked:', task);
        // You can add navigation or modal opening logic here
    };

    // Handle task edit
    const handleTaskEdit = task => {
        console.log('Task edit:', task);
        // You can add edit logic here
    };

    // Handle task delete
    const handleTaskDelete = task => {
        console.log('Task delete:', task);
        // You can add delete logic here
    };

    return (
        <div className="mt-3">
            {/* Filter Header */}
            <div className={styles.filterHeaderTop}>
                <Menu>
                    <MenuTrigger>
                        <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                            <Filter20Regular className="me-1" />
                            Filter ({filters.filter(f => f.column && f.value).length})
                        </span>
                    </MenuTrigger>
                    <MenuPopover style={{ width: '280px', minWidth: '280px' }}>
                        <MenuList>
                            <div className="p-2">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h6 className={styles.tableFilterHeader + ' mb-0'}>Filter Items By</h6>
                                    {filters.some(filter => filter.column && filter.value) && (
                                        <span className={styles.secondaryBtnText} onClick={handleClearAllFilters}>
                                            Clear All
                                        </span>
                                    )}
                                </div>

                                {filters.map(filter => (
                                    <div key={filter.id} className="d-flex align-items-center gap-2 mb-2">
                                        <select
                                            className={'form-select form-select-sm ' + styles.filterSelect}
                                            value={filter.column}
                                            onChange={e => handleUpdateFilter(filter.id, 'column', e.target.value)}>
                                            <option value="">Select Column</option>
                                            {getColumnOptions().map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>

                                        <select
                                            className={'form-select form-select-sm ' + styles.filterSelect}
                                            value={filter.value}
                                            onChange={e => handleUpdateFilter(filter.id, 'value', e.target.value)}
                                            disabled={!filter.column}>
                                            <option value="">Filter</option>
                                            {getValueOptions(filter.column).map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>

                                        {filters.length > 1 && (
                                            <button className="btn btn-link p-0" onClick={() => handleRemoveFilter(filter.id)} style={{ color: '#6c757d' }}>
                                                <Delete16Regular style={{ width: '16px', height: '16px' }} />
                                            </button>
                                        )}
                                    </div>
                                ))}

                                <span className={styles.secondaryBtnText + ' ms-1'} onClick={handleAddFilter}>
                                    Add another filter
                                </span>
                            </div>
                        </MenuList>
                    </MenuPopover>
                </Menu>
            </div>
            <div className="row mt-4">
                <div className="col-sm-12 col-md-4">
                    <div className="row ">
                        <div className="col-sm-12 col-md-6">
                            <div className={styles.reportingCardHeader}>
                                <span>All Tasks</span>
                            </div>
                            <div className={styles.reportingCardBody} style={{ height: 87 }}>
                                {stats.totalTasks}
                            </div>
                        </div>
                        <div className="col-sm-12 col-md-6">
                            <div className={styles.reportingCardHeader} style={{ background: 'var(--RAG-Amber)' }}>
                                <span>In Progress</span>
                            </div>
                            <div className={styles.reportingCardBody} style={{ height: 87 }}>
                                {stats.inProgress}
                            </div>
                        </div>
                        <div className="col-sm-12 col-md-6 mt-3">
                            <div className={styles.reportingCardHeader} style={{ background: 'var(--RAG-Red)' }}>
                                <span>Blocked</span>
                            </div>
                            <div className={styles.reportingCardBody} style={{ height: 87 }}>
                                {stats.stuck}
                            </div>
                        </div>
                        <div className="col-sm-12 col-md-6 mt-3">
                            <div className={styles.reportingCardHeader} style={{ background: 'var(--RAG-Green)' }}>
                                <span>Completed</span>
                            </div>
                            <div className={styles.reportingCardBody} style={{ height: 87 }}>
                                {stats.completed}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-sm-12 col-md-8">
                    <div className="row">
                        <div className="col-sm-12 col-md-6">
                            <div className={styles.reportingCardHeader}>
                                <span>Tasks by Status</span>
                            </div>
                            <div className={styles.reportingCardBody}>
                                <div className="d-flex align-items-center justify-content-center">
                                    <DonutChart
                                        data={fullTaskStatusChartItems.map(item => item.count)}
                                        colors={fullTaskStatusChartItems.map(item => item.color)}
                                        width={160}
                                        height={160}
                                        strokeWidth={20}
                                    />

                                    <div>
                                        {taskStatusLegendItems.map(item => (
                                            <div key={item.label} className="d-flex align-items-center">
                                                <div className={styles.ragCircle} style={{ background: item.color }}>
                                                    {item.percentage}%
                                                </div>
                                                <span className={styles.ragLabel}>{item.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-12 col-md-6">
                            <div className={styles.reportingCardHeader}>
                                <span>Tasks by Owner</span>
                            </div>
                            <div className={styles.reportingCardBody} style={{ justifyContent: 'initial', display: 'block', height: 232, overflowY: 'auto' }}>
                                {ownerStats.length > 0 ? (
                                    ownerStats.map((owner, index) => (
                                        <div key={index} className="d-flex align-items-center mb-3">
                                            <div style={{ width: '25px' }}>
                                                <CustomAvatar name={owner.name} size={24} />
                                            </div>
                                            <RagBarChart
                                                values={[owner.notStarted, owner.inProgress, owner.stuck, owner.completed, owner.cancelled, owner.undefinedStatus]}
                                                colors={['#c0c5d9', 'var(--RAG-Amber)', 'var(--RAG-Red)', 'var(--RAG-Green)', '#989BAD', '#bdbdbd']}
                                            />
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center text-muted">
                                        <p style={{ fontSize: 16 }}>No tasks assigned to users yet</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.reportingTableContainer}>
                <div className={styles.wsTableHeader}>
                    <span>Workstream Progress Overview</span>
                </div>
                <div className={styles.reportingTableBody}>
                    {workstreamStats && workstreamStats.length > 0 ? (
                        <div className="table-responsive">
                            <table className={styles.reportingTable}>
                                <thead>
                                    <tr>
                                        <th>Workstream</th>
                                        <th>Total Tasks</th>
                                        <th>Complete</th>
                                        <th>In Progress</th>
                                        <th>Blocked</th>
                                        <th>Not started</th>
                                        <th>Cancelled</th>
                                        <th>Last updated</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {workstreamStats.map(workstream => (
                                        <tr key={workstream.name}>
                                            <td>{workstream.name}</td>
                                            <td>{workstream.total}</td>
                                            <td>
                                                {workstream.completed > 0 ? (
                                                    <Tooltip
                                                        positioning="below"
                                                        relationship="description"
                                                        content={
                                                            <div className={styles.inProgressTooltip}>
                                                                <div className={styles.inProgressTooltipTitle}>Completed</div>
                                                                {Object.entries(workstream.completedByOwner || {}).map(([ownerName, count]) => (
                                                                    <div key={ownerName} className={styles.inProgressTooltipRow}>
                                                                        <CustomAvatar name={ownerName} size={24} />
                                                                        <span className={styles.inProgressTooltipCount}>
                                                                            {count} {count === 1 ? 'task' : 'tasks'}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        }>
                                                        <span className={styles.inProgressCell}>{workstream.completed}</span>
                                                    </Tooltip>
                                                ) : (
                                                    workstream.completed
                                                )}
                                            </td>
                                            <td>
                                                {workstream.inProgress > 0 ? (
                                                    <Tooltip
                                                        positioning="below"
                                                        relationship="description"
                                                        content={
                                                            <div className={styles.inProgressTooltip}>
                                                                <div className={styles.inProgressTooltipTitle}>In Progress</div>
                                                                {Object.entries(workstream.inProgressByOwner || {}).map(([ownerName, count]) => (
                                                                    <div key={ownerName} className={styles.inProgressTooltipRow}>
                                                                        <CustomAvatar name={ownerName} size={24} />
                                                                        <span className={styles.inProgressTooltipCount}>
                                                                            {count} {count === 1 ? 'task' : 'tasks'}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        }>
                                                        <span className={styles.inProgressCell}>{workstream.inProgress}</span>
                                                    </Tooltip>
                                                ) : (
                                                    workstream.inProgress
                                                )}
                                            </td>
                                            <td>
                                                {workstream.stuck > 0 ? (
                                                    <Tooltip
                                                        positioning="below"
                                                        relationship="description"
                                                        content={
                                                            <div className={styles.inProgressTooltip}>
                                                                <div className={styles.inProgressTooltipTitle}>Blocked</div>
                                                                {Object.entries(workstream.stuckByOwner || {}).map(([ownerName, count]) => (
                                                                    <div key={ownerName} className={styles.inProgressTooltipRow}>
                                                                        <CustomAvatar name={ownerName} size={24} />
                                                                        <span className={styles.inProgressTooltipCount}>
                                                                            {count} {count === 1 ? 'task' : 'tasks'}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        }>
                                                        <span className={styles.inProgressCell}>{workstream.stuck}</span>
                                                    </Tooltip>
                                                ) : (
                                                    workstream.stuck
                                                )}
                                            </td>
                                            <td>
                                                {workstream.notStarted > 0 ? (
                                                    <Tooltip
                                                        positioning="below"
                                                        relationship="description"
                                                        content={
                                                            <div className={styles.inProgressTooltip}>
                                                                <div className={styles.inProgressTooltipTitle}>Not started</div>
                                                                {Object.entries(workstream.notStartedByOwner || {}).map(([ownerName, count]) => (
                                                                    <div key={ownerName} className={styles.inProgressTooltipRow}>
                                                                        <CustomAvatar name={ownerName} size={24} />
                                                                        <span className={styles.inProgressTooltipCount}>
                                                                            {count} {count === 1 ? 'task' : 'tasks'}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        }>
                                                        <span className={styles.inProgressCell}>{workstream.notStarted}</span>
                                                    </Tooltip>
                                                ) : (
                                                    workstream.notStarted
                                                )}
                                            </td>
                                            <td>
                                                {workstream.cancelled > 0 ? (
                                                    <Tooltip
                                                        positioning="below"
                                                        relationship="description"
                                                        content={
                                                            <div className={styles.inProgressTooltip}>
                                                                <div className={styles.inProgressTooltipTitle}>Cancelled</div>
                                                                {Object.entries(workstream.cancelledByOwner || {}).map(([ownerName, count]) => (
                                                                    <div key={ownerName} className={styles.inProgressTooltipRow}>
                                                                        <CustomAvatar name={ownerName} size={24} />
                                                                        <span className={styles.inProgressTooltipCount}>
                                                                            {count} {count === 1 ? 'task' : 'tasks'}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        }>
                                                        <span className={styles.inProgressCell}>{workstream.cancelled}</span>
                                                    </Tooltip>
                                                ) : (
                                                    workstream.cancelled
                                                )}
                                            </td>
                                            <td>{formatWorkstreamLastUpdated(workstream.lastUpdated)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-3 text-muted" style={{ fontSize: 12 }}>
                            No workstream data available yet
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-4">
                <KanbanView
                    tasks={taskData}
                    onStatusChange={handleStatusChange}
                    projectUsersAll={projectUsersAll}
                    onTaskClick={handleTaskClick}
                    onTaskEdit={handleTaskEdit}
                    onTaskDelete={handleTaskDelete}
                    onOpenSidebar={onOpenSidebar}
                    previewTaskId={previewTaskId}
                />
            </div>

            <PauseTaskConfirmModal
                open={Boolean(pendingStatusChange)}
                onClose={() => !statusChangeLoading && setPendingStatusChange(null)}
                onThisOnly={handleStatusThisOnly}
                onWithFollowing={handleStatusWithFollowing}
                confirmLoading={statusChangeLoading}
                action={pendingStatusChange?.action || 'pause'}
            />

            <RecalculateScheduleModal
                open={Boolean(pendingScheduleChange)}
                onClose={() => !scheduleChangeLoading && setPendingScheduleChange(null)}
                onUpdateOnly={() => applyScheduleChange({ updateOnly: true })}
                onRecalculateDownstream={({ adjustDeadlines } = {}) =>
                    applyScheduleChange({ updateOnly: false, adjustDownstreamDeadlines: Boolean(adjustDeadlines) })
                }
                fieldLabel={pendingScheduleChange?.fieldLabel || 'Status'}
                affectedCount={pendingScheduleChange?.affectedCount}
                confirmLoading={scheduleChangeLoading}
            />
        </div>
    );
};

const RagBarChart = ({ values, colors }) => {
    const total = values.reduce((sum, val) => sum + val, 0);

    // Calculate width as percentage of maximum possible total
    const maxTotal = 1000; // Adjust this based on your data range
    const widthPercentage = Math.min((total / maxTotal) * 100, 100);

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    width: `${widthPercentage}%`,
                    height: '12px',
                    marginLeft: '12px',
                    overflow: 'hidden',
                    minWidth: '250px',
                }}>
                {values.map((value, index) => {
                    const totalValue = values.reduce((a, b) => a + b, 0);
                    const width = `${(value / totalValue) * 100}%`;
                    return (
                        <div
                            key={index}
                            style={{
                                width: width,
                                height: '100%',
                                backgroundColor: colors[index],
                                transition: 'width 0.3s ease',
                            }}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default Reporting;
